import { useCallback, useRef, useState } from 'react';
import type { WhatsAppMessageSendAttempt } from 'src/modules/communications/types/whatsapp/message-box.types';

/**
 * Keeps draft revisions and capture ownership outside sender availability changes.
 * @returns Controlled text, a capture flag, and a synchronous submission guard.
 */
export const useWhatsAppComposerDraft = () => {
  const [messageContent, setMessageContent] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const draftRevision = useRef(0);
  const draftContent = useRef('');
  const activeSend = useRef<symbol | null>(null);

  const changeMessageContent = useCallback((content: string) => {
    draftRevision.current += 1;
    draftContent.current = content;
    setMessageContent(content);
  }, []);

  const startMessageSend = useCallback(
    (content: string): WhatsAppMessageSendAttempt | undefined => {
      if (
        activeSend.current ||
        content.trim() !== draftContent.current.trim()
      ) {
        return undefined;
      }

      const attempt = Symbol();
      const submittedRevision = draftRevision.current;
      activeSend.current = attempt;
      setIsSendingMessage(true);

      const finish = (succeeded: boolean) => {
        if (activeSend.current !== attempt) return;

        if (succeeded && draftRevision.current === submittedRevision) {
          changeMessageContent('');
        }
        activeSend.current = null;
        setIsSendingMessage(false);
      };

      return { succeed: () => finish(true), fail: () => finish(false) };
    },
    [changeMessageContent]
  );

  return {
    messageContent,
    changeMessageContent,
    isSendingMessage,
    startMessageSend,
  };
};
